import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { notificacaoService } from '../services';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  BellRing,
  X, 
  AlertTriangle, 
  Clock, 
  Package,
  UserCheck,
  Calendar,
  CheckCheck,
  Eye,
  Trash2,
  Filter
} from 'lucide-react';

const NotificationModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [pagination, setPagination] = useState({});
  const [filtros, setFiltros] = useState({
    tipo: 'todos',
    status: 'todos'
  });
  const [showFiltros, setShowFiltros] = useState(false);

  const carregarNotificacoes = async (page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setCarregandoMais(true);
      }

      const params = {
        page,
        limit: 10
      };

      // Aplicar filtros
      if (filtros.tipo !== 'todos') {
        params.tipo = filtros.tipo;
      }
      if (filtros.status === 'lidas') {
        params.apenas_lidas = true;
      } else if (filtros.status === 'nao_lidas') {
        params.apenas_nao_lidas = true;
      }

      const response = await notificacaoService.getAll(params);
      
      if (page === 1) {
        setNotificacoes(response.notificacoes || []);
      } else {
        setNotificacoes(prev => [...prev, ...(response.notificacoes || [])]);
      }
      
      setPagination(response.pagination || {});
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
    } finally {
      setLoading(false);
      setCarregandoMais(false);
    }
  };

  useEffect(() => {
    if (isOpen && user?.cargo !== 'visitante') {
      carregarNotificacoes();
    }
  }, [isOpen, user?.cargo, filtros]);

  const marcarComoLida = async (id) => {
    try {
      await notificacaoService.marcarComoLida(id);
      setNotificacoes(prev => 
        prev.map(notif => 
          notif.id === id ? { ...notif, lida: true } : notif
        )
      );
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const marcarTodasComoLidas = async () => {
    try {
      await notificacaoService.marcarTodasComoLidas();
      setNotificacoes(prev => 
        prev.map(notif => ({ ...notif, lida: true }))
      );
    } catch (error) {
      console.error('Erro ao marcar todas notificações como lidas:', error);
    }
  };

  const apagarNotificacao = async (id) => {
    try {
      await notificacaoService.apagarNotificacao(id);
      setNotificacoes(prev => 
        prev.filter(notif => notif.id !== id)
      );
    } catch (error) {
      console.error('Erro ao apagar notificação:', error);
    }
  };

  const verNotificacao = (notificacao) => {
    // Marcar como lida se não estiver
    if (!notificacao.lida) {
      marcarComoLida(notificacao.id);
    }

    // Redirecionar baseado no tipo
    switch (notificacao.tipo) {
      case 'liberacao_pendente':
      case 'liberacao_agendada':
        if (notificacao.referencia_id) {
          navigate(`/liberacoes/detalhes/${notificacao.referencia_id}`);
        } else {
          navigate('/liberacoes');
        }
        break;
      case 'estoque_baixo':
      case 'medicamento_vencido':
        if (notificacao.referencia_id) {
          navigate(`/medicamentos/editar/${notificacao.referencia_id}`);
        } else {
          navigate('/medicamentos');
        }
        break;
      default:
        navigate('/');
    }
    
    onClose();
  };

  const handleNotificacaoClick = async (notificacao) => {
    // Marcar como lida se não estiver
    if (!notificacao.lida) {
      await marcarComoLida(notificacao.id);
    }
  };

  const carregarMais = () => {
    if (pagination.currentPage < pagination.totalPages) {
      carregarNotificacoes(pagination.currentPage + 1);
    }
  };

  const getIconePrioridade = (tipo, prioridade) => {
    const iconProps = { size: 20 };
    
    switch (tipo) {
      case 'liberacao_pendente':
        return <UserCheck {...iconProps} style={{ color: '#007bff' }} />;
      case 'estoque_baixo':
        return <Package {...iconProps} style={{ color: '#ffc107' }} />;
      case 'medicamento_vencido':
        return <AlertTriangle {...iconProps} style={{ color: '#dc3545' }} />;
      case 'liberacao_agendada':
        return <Calendar {...iconProps} style={{ color: '#28a745' }} />;
      default:
        return <Bell {...iconProps} style={{ color: '#6c757d' }} />;
    }
  };

  const getCorPrioridade = (prioridade) => {
    switch (prioridade) {
      case 'critica': return '#dc3545';
      case 'alta': return '#fd7e14';
      case 'media': return '#ffc107';
      case 'baixa': return '#28a745';
      default: return '#6c757d';
    }
  };

  const formatarData = (data) => {
    const agora = new Date();
    const dataNotificacao = new Date(data);
    const diferencaMs = agora - dataNotificacao;
    const diferencaMinutos = Math.floor(diferencaMs / (1000 * 60));
    const diferencaHoras = Math.floor(diferencaMs / (1000 * 60 * 60));
    const diferencaDias = Math.floor(diferencaMs / (1000 * 60 * 60 * 24));

    if (diferencaMinutos < 1) {
      return 'Agora mesmo';
    } else if (diferencaMinutos < 60) {
      return `${diferencaMinutos} min atrás`;
    } else if (diferencaHoras < 24) {
      return `${diferencaHoras}h atrás`;
    } else if (diferencaDias === 1) {
      return 'Ontem';
    } else if (diferencaDias < 7) {
      return `${diferencaDias} dias atrás`;
    } else {
      return dataNotificacao.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: 1050,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '60px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        width: '500px',
        maxWidth: '90vw',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Bell size={24} />
            Notificações
          </h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => setShowFiltros(!showFiltros)}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Filtros"
            >
              <Filter size={16} />
              Filtros
            </button>
            <button
              onClick={marcarTodasComoLidas}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Marcar todas como lidas"
            >
              <CheckCheck size={16} />
              Marcar todas
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6c757d'
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filtros */}
        {showFiltros && (
          <div style={{
            padding: '16px 24px',
            borderBottom: '1px solid #f8f9fa',
            backgroundColor: '#fafafa'
          }}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>
                  Tipo:
                </label>
                <select
                  value={filtros.tipo}
                  onChange={(e) => setFiltros(prev => ({ ...prev, tipo: e.target.value }))}
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  <option value="todos">Todos</option>
                  <option value="estoque_baixo">Estoque Baixo</option>
                  <option value="medicamento_vencido">Medicamentos Vencidos</option>
                  {(user?.cargo === 'admin' || user?.cargo === 'farmaceutico') && (
                    <option value="liberacao_pendente">Liberações Pendentes</option>
                  )}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>
                  Status:
                </label>
                <select
                  value={filtros.status}
                  onChange={(e) => setFiltros(prev => ({ ...prev, status: e.target.value }))}
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  <option value="todos">Todas</option>
                  <option value="nao_lidas">Não Lidas</option>
                  <option value="lidas">Lidas</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Body */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px 0'
        }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto 16px auto' }}></div>
              <p style={{ color: '#667' }}>Carregando notificações...</p>
            </div>
          ) : notificacoes.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#667' }}>
              <Bell size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
              <h4>Nenhuma notificação</h4>
              <p>Você está em dia com todas as notificações!</p>
            </div>
          ) : (
            <>
              {notificacoes.map((notif) => (
                <div
                  key={notif.id}
                  style={{
                    padding: '16px 24px',
                    borderBottom: '1px solid #f8f9fa',
                    backgroundColor: notif.lida ? 'transparent' : '#f8f9fa',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onClick={() => handleNotificacaoClick(notif)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = notif.lida ? 'transparent' : '#f8f9fa';
                  }}
                >
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, marginTop: '2px' }}>
                      {getIconePrioridade(notif.tipo, notif.prioridade)}
                    </div>
                    
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '4px'
                      }}>
                        <h5 style={{ 
                          margin: 0, 
                          fontSize: '14px', 
                          fontWeight: notif.lida ? 'normal' : 'bold',
                          color: notif.lida ? '#667' : '#333',
                          flex: 1
                        }}>
                          {notif.titulo}
                        </h5>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
                          <span style={{
                            backgroundColor: getCorPrioridade(notif.prioridade),
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase'
                          }}>
                            {notif.prioridade}
                          </span>
                          
                          {!notif.lida && (
                            <div style={{
                              width: '8px',
                              height: '8px',
                              backgroundColor: '#007bff',
                              borderRadius: '50%'
                            }} />
                          )}
                        </div>
                      </div>
                      
                      <p style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: '13px', 
                        color: notif.lida ? '#999' : '#555',
                        lineHeight: '1.4'
                      }}>
                        {notif.descricao}
                      </p>
                      
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '4px',
                          fontSize: '12px',
                          color: '#999'
                        }}>
                          <Clock size={12} />
                          {formatarData(notif.createdAt)}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => verNotificacao(notif)}
                            style={{
                              background: '#007bff',
                              border: 'none',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Ver detalhes"
                          >
                            <Eye size={12} />
                            Ver
                          </button>
                          <button
                            onClick={() => apagarNotificacao(notif.id)}
                            style={{
                              background: '#dc3545',
                              border: 'none',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Apagar notificação"
                          >
                            <Trash2 size={12} />
                            Apagar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {pagination.currentPage < pagination.totalPages && (
                <div style={{ padding: '16px 24px', textAlign: 'center' }}>
                  <button
                    onClick={carregarMais}
                    disabled={carregandoMais}
                    className="btn btn-outline-primary btn-sm"
                  >
                    {carregandoMais ? 'Carregando...' : 'Carregar mais'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;